import * as React from 'react';
import { Draft, Inline, Link, PopoutAlt, Stack, Text } from '@resi-media/resi-ui';
import { CopyToClipboard } from '@studio/components/CopyToClipboard';
import { usePrefix } from '@studio/hooks/use-prefix';
import { buildGcpLogsUrl } from '@studio/helpers/buildGcpLogsUrl';

type _State = {
  message: string;
  name: string;
  requestId: string;
  statusCode: number;
};

const ServerErrorDetailsModalInternal = () => {
  const { commonT, linkT, prefixNS } = usePrefix('components:', 'serverErrorDetails');
  const { message, name, onCloseReset, requestId, statusCode } = Draft.ModalContext.useModal<_State>();

  const statusDisplay = `${statusCode} ${name}`;
  const gcpLogsUrl = requestId ? buildGcpLogsUrl(requestId) : '';

  return (
    <React.Fragment>
      <Draft.ModalHeader
        dataTestId="server-error-details-header"
        header={prefixNS('title')}
        onClose={onCloseReset}
        showBorder
      />
      <Draft.ModalBody dataTestId="server-error-details-body">
        <Stack gap="l">
          <Text dataTestId="server-error-details-description">
            {prefixNS('description')}{' '}
            <Link data-testid="server-error-details-contact-link" target="_blank" href={linkT('contact')}>
              {commonT('contactSupport')}
            </Link>
          </Text>
          <Draft.FormField dataTestId="server-error-details-tracking-id-field" fieldLabel={prefixNS('trackingId')}>
            <Inline alignItems="center" gap="m">
              <Draft.TextInput data-testid="server-error-details-tracking-id-input" readOnly value={requestId} />
              <CopyToClipboard
                dataTestId="server-error-details-tracking-id-copy"
                name={prefixNS('trackingId')}
                text={requestId}
                iconButtonProps={{
                  sizeVariant: 'm',
                  variant: 'outlined',
                }}
              />
            </Inline>
          </Draft.FormField>
          <Draft.FormField dataTestId="server-error-details-status-field" fieldLabel={commonT('status')}>
            <Draft.TextInput data-testid="server-error-details-status-input" readOnly value={statusDisplay} />
          </Draft.FormField>
          <Draft.FormField dataTestId="server-error-details-message-field" fieldLabel={commonT('message')}>
            <Draft.TextInput data-testid="server-error-details-message-input" readOnly value={message} as="textarea" />
          </Draft.FormField>
        </Stack>
      </Draft.ModalBody>
      <Draft.ModalFooter dataTestId="server-error-details-footer" showBorder>
        <Inline gap="m" justifyContent="flex-end">
          <Draft.Button
            dataTestId="server-error-details-close-button"
            label={commonT('close')}
            onClick={onCloseReset}
            variant="ghost"
          />
          {gcpLogsUrl && (
            <Draft.Button
              as="a"
              dataTestId="server-error-details-open-log-button"
              href={gcpLogsUrl}
              label={prefixNS('openLog')}
              rel="noreferrer noopener"
              target="_blank"
              variant="outlined"
              endNode={<PopoutAlt />}
            />
          )}
        </Inline>
      </Draft.ModalFooter>
    </React.Fragment>
  );
};

ServerErrorDetailsModalInternal.displayName = 'ServerErrorDetailsModalInternal';

/* eslint-disable import/export */
export const ServerErrorDetailsModal = Object.assign(React.forwardRef(ServerErrorDetailsModalInternal), {
  displayName: 'ServerErrorDetailsModal',
});

// eslint-disable-next-line no-redeclare
export namespace ServerErrorDetailsModal {
  export type State = _State;
}
